import { useParams, Link } from 'react-router-dom';
import { useMemo, useState } from 'react';
import { useMaterial } from '../../hooks/useMaterial';
import { relativeTime } from '@news-assist/shared';

const getOrgPrefix = (orgSlug?: string) => orgSlug ? `/${orgSlug}` : '';

interface HighlightMatch {
  type: 'claim' | 'quote';
  id: string;
  content: string;
  start: number;
  end: number;
  metadata: {
    claimType?: string;
    speakerName?: string;
    isDirect?: boolean;
    isNovel?: boolean;
    confidenceScore?: number;
    sourceTexts?: string[];
  };
}

export function MaterialPage() {
  const { id, orgSlug } = useParams<{ id: string; orgSlug: string }>();
  const orgPrefix = getOrgPrefix(orgSlug);
  const { data: material, isLoading, isError, error } = useMaterial(id!);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showClaims, setShowClaims] = useState(true);
  const [showQuotes, setShowQuotes] = useState(true);

  // Find positions of claims and quotes in the material content
  const highlights = useMemo(() => {
    if (!material) return [];

    const matches: HighlightMatch[] = [];
    const content = material.raw_content.toLowerCase();

    // Find claims in the content
    if (showClaims) {
      for (const claim of material.claims) {
        // Parse source_texts if available (stored as JSON string)
        // Also check metadata.source_text as fallback
        let sourceTexts: string[] = [];
        if (claim.source_texts) {
          sourceTexts = JSON.parse(claim.source_texts);
        } else if (claim.metadata) {
          try {
            const meta = typeof claim.metadata === 'string' ? JSON.parse(claim.metadata) : claim.metadata;
            if (meta.source_text) {
              sourceTexts = [meta.source_text];
            }
          } catch {}
        }

        // If we have source_texts, use them for precise highlighting
        if (sourceTexts.length > 0) {
          for (const sourceText of sourceTexts) {
            const idx = material.raw_content.indexOf(sourceText);
            if (idx !== -1) {
              matches.push({
                type: 'claim',
                id: claim.id,
                content: claim.content,
                start: idx,
                end: idx + sourceText.length,
                metadata: {
                  claimType: claim.claim_type,
                  isNovel: claim.is_novel === 1,
                  confidenceScore: claim.confidence_score,
                  sourceTexts,
                },
              });
            }
          }
        } else {
          // Fallback to fuzzy matching for old claims without source_texts
          const normalizedClaim = claim.content.toLowerCase().replace(/[^\w\s]/g, '');
          const words = normalizedClaim.split(/\s+/).filter(w => w.length > 3);

          // Try to find a sequence of words from the claim
          if (words.length >= 3) {
            const searchPhrase = words.slice(0, 5).join('\\s+');
            const regex = new RegExp(searchPhrase, 'i');
            const match = material.raw_content.match(regex);

            if (match && match.index !== undefined) {
              // Extend to find the full sentence/clause
              let start = match.index;
              let end = match.index + match[0].length;

              // Extend backwards to sentence start
              while (start > 0 && !['.', '!', '?', '\n'].includes(material.raw_content[start - 1] || '')) {
                start--;
              }
              // Extend forwards to sentence end
              while (end < material.raw_content.length && !['.', '!', '?', '\n'].includes(material.raw_content[end] || '')) {
                end++;
              }
              const endChar = material.raw_content[end] || '';
              if (['.', '!', '?'].includes(endChar)) end++;

              matches.push({
                type: 'claim',
                id: claim.id,
                content: claim.content,
                start,
                end,
                metadata: {
                  claimType: claim.claim_type,
                  isNovel: claim.is_novel === 1,
                  confidenceScore: claim.confidence_score,
                },
              });
            }
          }
        }
      }
    }

    // Find quotes in the content
    if (showQuotes) {
      for (const quote of material.quotes) {
        // Quotes are usually exact matches with quotation marks
        const quoteContent = quote.content;
        const idx = material.raw_content.indexOf(quoteContent);

        if (idx !== -1) {
          matches.push({
            type: 'quote',
            id: quote.id,
            content: quote.content,
            start: idx,
            end: idx + quoteContent.length,
            metadata: {
              speakerName: quote.speaker_name,
              isDirect: quote.is_direct === 1,
              isNovel: quote.is_novel === 1,
            },
          });
        } else {
          // Try fuzzy match with first few words
          const words = quoteContent.split(/\s+/).slice(0, 4).join(' ');
          const fuzzyIdx = material.raw_content.toLowerCase().indexOf(words.toLowerCase());
          if (fuzzyIdx !== -1) {
            matches.push({
              type: 'quote',
              id: quote.id,
              content: quote.content,
              start: fuzzyIdx,
              end: fuzzyIdx + quoteContent.length,
              metadata: {
                speakerName: quote.speaker_name,
                isDirect: quote.is_direct === 1,
                isNovel: quote.is_novel === 1,
              },
            });
          }
        }
      }
    }

    // Sort by position and remove overlaps
    matches.sort((a, b) => a.start - b.start);

    const nonOverlapping: HighlightMatch[] = [];
    for (const match of matches) {
      const last = nonOverlapping[nonOverlapping.length - 1];
      if (!last || match.start >= last.end) {
        nonOverlapping.push(match);
      }
    }

    return nonOverlapping;
  }, [material, showClaims, showQuotes]);

  // Render content with highlights
  const renderedContent = useMemo(() => {
    if (!material) return null;

    const parts: React.ReactElement[] = [];
    let lastEnd = 0;

    for (const highlight of highlights) {
      // Add text before this highlight
      if (highlight.start > lastEnd) {
        parts.push(
          <span key={`text-${lastEnd}`}>
            {material.raw_content.slice(lastEnd, highlight.start)}
          </span>
        );
      }

      // Add the highlighted text
      const isSelected = selectedId === highlight.id;
      const highlightClasses = highlight.type === 'claim'
        ? `bg-yellow-100 border-b-2 border-yellow-400 cursor-pointer hover:bg-yellow-200 ${isSelected ? 'bg-yellow-300 ring-2 ring-yellow-500' : ''}`
        : `bg-purple-100 border-b-2 border-purple-400 cursor-pointer hover:bg-purple-200 ${isSelected ? 'bg-purple-300 ring-2 ring-purple-500' : ''}`;

      parts.push(
        <mark
          key={highlight.id}
          className={highlightClasses}
          onClick={() => setSelectedId(isSelected ? null : highlight.id)}
          title={highlight.type === 'claim' ? `Claim: ${highlight.content}` : `Quote by ${highlight.metadata.speakerName}`}
        >
          {material.raw_content.slice(highlight.start, highlight.end)}
        </mark>
      );

      lastEnd = highlight.end;
    }

    // Add remaining text
    if (lastEnd < material.raw_content.length) {
      parts.push(
        <span key={`text-${lastEnd}`}>
          {material.raw_content.slice(lastEnd)}
        </span>
      );
    }

    return parts;
  }, [material, highlights, selectedId]);

  // Find selected item - first check highlights, then fall back to claims/quotes list
  // Must be before early returns to maintain hook order
  const selectedHighlight = highlights.find(h => h.id === selectedId);
  const selectedItem = useMemo(() => {
    if (selectedHighlight) return selectedHighlight;
    if (!selectedId || !material) return null;

    // Check claims
    const claim = material.claims.find(c => c.id === selectedId);
    if (claim) {
      return {
        type: 'claim' as const,
        id: claim.id,
        content: claim.content,
        start: -1,
        end: -1,
        metadata: {
          claimType: claim.claim_type,
          isNovel: claim.is_novel === 1,
          confidenceScore: claim.confidence_score,
          sourceTexts: claim.source_texts ? JSON.parse(claim.source_texts) : [],
        },
      };
    }

    // Check quotes
    const quote = material.quotes.find(q => q.id === selectedId);
    if (quote) {
      return {
        type: 'quote' as const,
        id: quote.id,
        content: quote.content,
        start: -1,
        end: -1,
        metadata: {
          speakerName: quote.speaker_name,
          isDirect: quote.is_direct === 1,
          isNovel: quote.is_novel === 1,
        },
      };
    }

    return null;
  }, [selectedId, selectedHighlight, material]);

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <div className="text-gray-500">Loading material...</div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
        Error: {error.message}
      </div>
    );
  }

  if (!material) {
    return <div className="text-gray-500">Material not found</div>;
  }

  return (
    <div className="flex gap-6">
      {/* Main material content */}
      <div className="flex-1">
        <div className="bg-white rounded-lg shadow-sm p-6">
          {/* Header */}
          <div className="mb-6 border-b pb-4">
            <div className="flex items-center gap-2 text-sm text-gray-500 mb-2">
              <span className="px-2 py-0.5 bg-gray-100 rounded">{material.source_name}</span>
              {material.published_at && (
                <time>{relativeTime(material.published_at)}</time>
              )}
              {material.story_title && (
                <>
                  <span>in</span>
                  <Link to={`${orgPrefix}/story/${material.story_id}`} className="text-blue-600 hover:underline">
                    {material.story_title}
                  </Link>
                </>
              )}
            </div>
            <h1 className="text-2xl font-bold text-gray-900">{material.title}</h1>
            {material.author && (
              <p className="text-sm text-gray-600 mt-1">By {material.author}</p>
            )}
            {material.url && (
              <a
                href={material.url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-blue-600 hover:underline mt-2 inline-block"
              >
                View original
              </a>
            )}
          </div>

          {/* Toggle controls */}
          <div className="mb-4 flex items-center gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={showClaims}
                onChange={(e) => setShowClaims(e.target.checked)}
                className="rounded border-gray-300 text-yellow-600 focus:ring-yellow-500"
              />
              <span className="text-sm">
                <span className="inline-block w-3 h-3 bg-yellow-200 border border-yellow-400 rounded mr-1"></span>
                Claims ({material.claims.length})
              </span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={showQuotes}
                onChange={(e) => setShowQuotes(e.target.checked)}
                className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
              />
              <span className="text-sm">
                <span className="inline-block w-3 h-3 bg-purple-200 border border-purple-400 rounded mr-1"></span>
                Quotes ({material.quotes.length})
              </span>
            </label>
          </div>

          {/* Material content with highlights */}
          <div className="prose max-w-none whitespace-pre-wrap leading-relaxed text-gray-800">
            {renderedContent}
          </div>
        </div>
      </div>

      {/* Sidebar with extractions */}
      <div className="w-80 flex-shrink-0">
        <div className="sticky top-4 space-y-4">
          {/* Selected item detail */}
          {selectedItem && (
            <div className={`p-4 rounded-lg ${selectedItem.type === 'claim' ? 'bg-yellow-50 border border-yellow-200' : 'bg-purple-50 border border-purple-200'}`}>
              <div className="flex items-center gap-2 mb-2">
                <span className={`text-xs font-medium px-2 py-0.5 rounded ${selectedItem.type === 'claim' ? 'bg-yellow-200 text-yellow-800' : 'bg-purple-200 text-purple-800'}`}>
                  {selectedItem.type === 'claim' ? 'CLAIM' : 'QUOTE'}
                </span>
                <button
                  onClick={() => setSelectedId(null)}
                  className="ml-auto text-gray-400 hover:text-gray-600"
                >
                  &times;
                </button>
              </div>
              <p className="text-sm text-gray-800 mb-2">{selectedItem.content}</p>
              <div className="text-xs text-gray-500 space-y-1">
                {selectedItem.metadata.claimType && (
                  <p>Type: {selectedItem.metadata.claimType}</p>
                )}
                {'speakerName' in selectedItem.metadata && selectedItem.metadata.speakerName && (
                  <p>Speaker: {selectedItem.metadata.speakerName}</p>
                )}
                {selectedItem.metadata.confidenceScore !== undefined && (
                  <p>Confidence: {Math.round(selectedItem.metadata.confidenceScore * 100)}%</p>
                )}
                {selectedItem.metadata.isNovel !== undefined && (
                  <p>Novel: {selectedItem.metadata.isNovel ? 'Yes' : 'No'}</p>
                )}
              </div>
              {'sourceTexts' in selectedItem.metadata && selectedItem.metadata.sourceTexts && selectedItem.metadata.sourceTexts.length > 0 && (
                <div className="mt-3 pt-3 border-t border-gray-200">
                  <p className="text-xs font-medium text-gray-600 mb-1">Source text(s):</p>
                  <div className="space-y-1">
                    {(selectedItem.metadata.sourceTexts as string[]).map((text: string, i: number) => (
                      <p key={i} className="text-xs text-gray-500 italic bg-gray-50 p-1 rounded">
                        "{text}"
                      </p>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Claims list */}
          <div className="bg-white rounded-lg shadow-sm p-4">
            <h3 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
              <span className="inline-block w-3 h-3 bg-yellow-200 border border-yellow-400 rounded"></span>
              Claims ({material.claims.length})
            </h3>
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {material.claims.map((claim) => (
                <button
                  key={claim.id}
                  onClick={() => setSelectedId(claim.id)}
                  className={`w-full text-left p-2 rounded text-sm transition-colors ${
                    selectedId === claim.id
                      ? 'bg-yellow-100 border border-yellow-300'
                      : 'hover:bg-gray-50 border border-transparent'
                  }`}
                >
                  <p className="line-clamp-2 text-gray-700">{claim.content}</p>
                  <span className="text-xs text-gray-400">{claim.claim_type}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Quotes list */}
          <div className="bg-white rounded-lg shadow-sm p-4">
            <h3 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
              <span className="inline-block w-3 h-3 bg-purple-200 border border-purple-400 rounded"></span>
              Quotes ({material.quotes.length})
            </h3>
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {material.quotes.map((quote) => (
                <button
                  key={quote.id}
                  onClick={() => setSelectedId(quote.id)}
                  className={`w-full text-left p-2 rounded text-sm transition-colors ${
                    selectedId === quote.id
                      ? 'bg-purple-100 border border-purple-300'
                      : 'hover:bg-gray-50 border border-transparent'
                  }`}
                >
                  <p className="line-clamp-2 text-gray-700 italic">"{quote.content}"</p>
                  <span className="text-xs text-gray-400">- {quote.speaker_name}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
